// CheckoutSuccess.jsx — FIXED
// Fixes: uploads logo (stored as sessionStorage data-URI) after verify-session
// zenithpost.ai branding throughout

import { useState, useEffect, useRef } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useDispatch } from "react-redux";
import { resetForm } from "../store/formSlice";

const API = process.env.REACT_APP_API_URL || "http://localhost:8000";

const D = {
  bg: "#0E0F14", card: "#191C26", border: "rgba(255,255,255,0.07)",
  accent: "#6C7EF8", accentDim: "rgba(108,126,248,0.12)",
  success: "#34D399", successDim: "rgba(52,211,153,0.1)",
  danger: "#F87171", purple: "#A378F8",
  textPrimary: "#F0F2F7", textSec: "#B0B7CE", textMuted: "#5A607A",
  fontDisplay: "'Plus Jakarta Sans', sans-serif",
  fontBody: "'DM Sans', sans-serif",
  fontMono: "'JetBrains Mono', monospace",
};

// ✅ Upload logo to backend after signup — using the data-URI stored in sessionStorage
async function uploadLogoAfterSignup(email, token) {   // ← add token param
  const dataUri  = sessionStorage.getItem("pending_logo_datauri");
  const mimeType = sessionStorage.getItem("pending_logo_mimetype") || "image/png";
  if (!dataUri || !email || !token) return;             // ← guard token too

  try {
    const byteString  = atob(dataUri.split(",")[1]);
    const byteNumbers = new Uint8Array(byteString.length);
    for (let i = 0; i < byteString.length; i++) byteNumbers[i] = byteString.charCodeAt(i);
    const blob = new Blob([byteNumbers], { type: mimeType });
    const ext  = mimeType.includes("png") ? "png" : mimeType.includes("webp") ? "webp" : "jpg";
    const file = new File([blob], `logo.${ext}`, { type: mimeType });

    const formData = new FormData();
    formData.append("sectionData", JSON.stringify({}));
    formData.append("logo", file);
    // ← remove formData.append("email", email) — backend reads email from token now

    const res = await fetch(`${API}/api/auth/brand-profile/section`, {
      method: "PATCH",
      headers: { "Authorization": `Bearer ${token}` },  // ← this is the fix
      body: formData,
    });

    if (res.ok) {
      sessionStorage.removeItem("pending_logo_datauri");
      sessionStorage.removeItem("pending_logo_mimetype");
    } else {
      console.warn("[CheckoutSuccess] Logo upload failed:", res.status, await res.text());
    }
  } catch (err) {
    console.warn("[CheckoutSuccess] Logo upload error:", err);
  }
}

export default function CheckoutSuccess() {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const hasCalled = useRef(false);

  const [status, setStatus] = useState("verifying");
  const [errMsg, setErrMsg] = useState("");
  const [step, setStep] = useState(""); // "verifying" | "uploading_logo" | "done"

  useEffect(() => {
    if (!sessionId) {
      setStatus("error");
      setErrMsg("No session ID found. If you completed payment, please contact support.");
      return;
    }
    if (hasCalled.current) return;
    hasCalled.current = true;
    verify();
  }, [sessionId]);

  const verify = async () => {
    setStatus("verifying");
    setStep("verifying");
    setErrMsg("");
    try {
      const res = await fetch(`${API}/api/auth/verify-session`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ session_id: sessionId }),
      });

      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body.detail || `Verification failed (${res.status})`);
      }

      const { token, email } = await res.json();
      localStorage.setItem("auth_token", token);
      localStorage.setItem("email", email.trim().toLowerCase());
      localStorage.removeItem("checkout_profile");
      dispatch(resetForm());

      // ✅ FIX: Upload logo now that user exists in DB
      const hasPendingLogo = sessionStorage.getItem("pending_logo_datauri");
      if (hasPendingLogo) {
        setStep("uploading_logo");
        await uploadLogoAfterSignup(email,token);
      }

      setStep("done");
      setStatus("success");
      setTimeout(() => navigate("/home", { replace: true }), 2500);

    } catch (e) {
      console.error("[CheckoutSuccess verify]", e);
      setStatus("error");
      setErrMsg(e.message || "We couldn't verify your payment. Please contact support.");
    }
  };

  const stepLabels = {
    verifying: "Confirming your payment…",
    uploading_logo: "Uploading your brand assets…",
    done: "All set! Taking you to your dashboard…",
  };

  return (
    <>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Plus+Jakarta+Sans:wght@600;700;800&family=DM+Sans:wght@400;500;600&family=JetBrains+Mono:wght@400;500&display=swap');
        *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }
        body { background:${D.bg}; color:${D.textPrimary}; font-family:${D.fontBody}; -webkit-font-smoothing:antialiased; }
        @keyframes spin     { to { transform:rotate(360deg); } }
        @keyframes fadeUp   { from{opacity:0;transform:translateY(18px)} to{opacity:1;transform:translateY(0)} }
        @keyframes pop      { 0%{transform:scale(0.7);opacity:0} 60%{transform:scale(1.1)} 100%{transform:scale(1);opacity:1} }
        @keyframes progress { from{width:0} to{width:100%} }
        @keyframes logoGlow { 0%,100%{box-shadow:0 0 20px rgba(108,126,248,0.3)} 50%{box-shadow:0 0 32px rgba(163,123,248,0.5)} }
        .fade-up { animation:fadeUp 0.5s cubic-bezier(0.22,1,0.36,1) both; }
        .page-glow { position:fixed;inset:0;z-index:0;pointer-events:none;background:radial-gradient(ellipse 60% 50% at 50% 0%,rgba(52,211,153,0.06) 0%,transparent 65%),radial-gradient(ellipse 40% 40% at 80% 80%,rgba(108,126,248,0.05) 0%,transparent 60%); }
        @media (max-width: 480px) {
          .success-card { padding: 28px 22px !important; }
        }
      `}</style>

      <div className="page-glow" />

      <div style={{ minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center", padding: "40px 20px", position: "relative", zIndex: 1 }}>
        <div style={{ width: "100%", maxWidth: 460, textAlign: "center" }}>

          {/* zenithpost Logo */}
          <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 10, marginBottom: 40 }}>
            <div style={{ width: 36, height: 36, borderRadius: 10, background: `linear-gradient(135deg,${D.accent} 0%,${D.purple} 100%)`, display: "flex", alignItems: "center", justifyContent: "center", fontFamily: D.fontDisplay, fontWeight: 800, fontSize: 18, color: "white", animation: "logoGlow 3s ease infinite" }}>Z</div>
            <span style={{ fontFamily: D.fontDisplay, fontWeight: 700, fontSize: 18, color: D.textPrimary }}>
              zenithpost<span style={{ color: D.accent }}>.ai</span>
            </span>
          </div>

          <div className="success-card" style={{ background: D.card, border: `1px solid ${D.border}`, borderRadius: 20, padding: "44px 40px", boxShadow: "0 24px 64px rgba(0,0,0,0.4)" }}>

            {status === "verifying" && (
              <>
                <div style={{ width: 60, height: 60, borderRadius: "50%", background: D.accentDim, border: `1px solid rgba(108,126,248,0.3)`, display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 24px" }}>
                  <div style={{ width: 26, height: 26, borderRadius: "50%", border: `2.5px solid rgba(108,126,248,0.3)`, borderTopColor: D.accent, animation: "spin 0.8s linear infinite" }} />
                </div>
                <h2 style={{ fontFamily: D.fontDisplay, fontWeight: 700, fontSize: 22, color: D.textPrimary, marginBottom: 10 }}>Setting up your account</h2>
                <p style={{ fontSize: 14, color: D.textSec, lineHeight: 1.65, marginBottom: 16 }}>
                  {step ? stepLabels[step] : "Verifying your payment…"}
                </p>
                {/* Steps indicator */}
                <div style={{ display: "flex", gap: 8, justifyContent: "center", mt: 8 }}>
                  {["verifying", "uploading_logo", "done"].map((s, i) => (
                    <div key={s} style={{ width: i === ["verifying", "uploading_logo", "done"].indexOf(step) ? 24 : 8, height: 4, borderRadius: 99, background: ["verifying", "uploading_logo", "done"].indexOf(step) >= i ? D.accent : "rgba(255,255,255,0.12)", transition: "all 0.35s" }} />
                  ))}
                </div>
              </>
            )}

            {status === "success" && (
              <div className="fade-up">
                <div style={{ width: 70, height: 70, borderRadius: "50%", background: D.successDim, border: `1px solid rgba(52,211,153,0.3)`, display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 24px", animation: "pop 0.5s cubic-bezier(0.22,1,0.36,1) both", fontSize: 32 }}>✓</div>
                <div style={{ fontFamily: D.fontMono, fontSize: 10.5, color: D.success, letterSpacing: "0.1em", textTransform: "uppercase", marginBottom: 10 }}>Payment confirmed</div>
                <h2 style={{ fontFamily: D.fontDisplay, fontWeight: 800, fontSize: 28, color: D.textPrimary, lineHeight: 1.2, marginBottom: 12 }}>Welcome to zenithpost.ai</h2>
                <p style={{ fontSize: 14, color: D.textSec, lineHeight: 1.65, marginBottom: 28 }}>
                  Your account is active and your brand profile is saved. Redirecting you to your content command center…
                </p>
                <div style={{ height: 2, borderRadius: 99, background: "rgba(255,255,255,0.06)", overflow: "hidden" }}>
                  <div style={{ height: "100%", background: `linear-gradient(90deg, ${D.success}, ${D.accent})`, borderRadius: 99, animation: "progress 2.5s linear both", boxShadow: `0 0 8px ${D.success}55` }} />
                </div>
              </div>
            )}

            {status === "error" && (
              <div className="fade-up">
                <div style={{ width: 60, height: 60, borderRadius: "50%", background: "rgba(248,113,113,0.1)", border: "1px solid rgba(248,113,113,0.25)", display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 24px", fontSize: 26 }}>⚠</div>
                <h2 style={{ fontFamily: D.fontDisplay, fontWeight: 700, fontSize: 22, color: D.textPrimary, marginBottom: 10 }}>Verification issue</h2>
                <p style={{ fontSize: 13.5, color: D.textSec, lineHeight: 1.65, marginBottom: 6 }}>{errMsg}</p>
                <p style={{ fontSize: 12.5, color: D.textMuted, lineHeight: 1.65, marginBottom: 28 }}>
                  If your card was charged, email <span style={{ color: D.accent }}>info@zenithpost.ai</span> with session ID:{" "}
                  <code style={{ fontFamily: D.fontMono, fontSize: 11, color: D.textMuted }}>{sessionId?.slice(0, 24)}…</code>
                </p>
                <div style={{ display: "flex", gap: 10 }}>
                  <button onClick={verify} style={{ flex: 1, padding: "11px 0", borderRadius: 10, border: `1px solid ${D.border}`, background: "rgba(255,255,255,0.04)", color: D.textSec, fontFamily: D.fontBody, fontSize: 13.5, fontWeight: 600, cursor: "pointer" }}>
                    Retry verification
                  </button>
                  <button onClick={() => navigate("/signup")} style={{ flex: 1, padding: "11px 0", borderRadius: 10, border: "none", background: `linear-gradient(135deg,${D.accent},${D.purple})`, color: "white", fontFamily: D.fontBody, fontSize: 13.5, fontWeight: 700, cursor: "pointer" }}>
                    Back to signup
                  </button>
                </div>
              </div>
            )}
          </div>

          {/* Footer */}
          <p style={{ fontFamily: D.fontMono, fontSize: 11, color: D.textMuted, marginTop: 24 }}>
            Secure payment powered by Stripe · zenithpost.ai © 2025
          </p>
        </div>
      </div>
    </>
  );
}